import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogTrigger 
} from '../components/ui/dialog';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '../components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { 
  Plus, 
  Trash2, 
  ShieldCheck, 
  Edit2, 
  GraduationCap, 
  Stethoscope, 
  Building2, 
  Landmark, 
  Shield,
  Receipt,
  FileText,
  Users,
  ShoppingBag,
  LayoutDashboard,
  UserPlus,
  MessageSquare,
  Newspaper,
  Award,
  Ban
} from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Permessi organizzati per categoria - SINCRONIZZATO CON BACKEND
const PERMESSI_CATEGORIE = {
  "Gestionale Azienda": {
    icon: Building2,
    permessi: {
      visualizza_gestionale_azienda: "Accede al Gestionale della propria Azienda",
      visualizza_scontrini: "Visualizza scontrini aziendali",
      crea_scontrini: "Crea nuovi scontrini",
      modifica_scontrini: "Modifica scontrini esistenti",
      elimina_scontrini: "Elimina scontrini"
    }
  },
  "Gestione Personale": {
    icon: Users,
    permessi: {
      gestisci_dipendenti: "Gestisce dipendenti (assumi, licenzia, modifica ruolo)",
      visualizza_assunzioni: "Visualizza sezione assunzioni",
      gestisci_assunzioni: "Può assumere nella propria azienda",
      gestisci_concorsi: "Gestisce concorsi dell'azienda",
      crea_concorsi_presenza: "Crea concorsi in presenza",
      crea_concorsi_gestionale: "Crea concorsi via gestionale",
      gestisci_curriculum: "Gestisce curriculum ricevuti",
      gestisci_ldl: "Gestisce domande di lavoro"
    }
  },
  "Moderazione": {
    icon: Ban,
    permessi: {
      gestisci_ban: "Può bannare/sbannare utenti"
    }
  },
  "Forum": {
    icon: MessageSquare,
    permessi: {
      visualizza_forum: "Visualizza il Forum",
      crea_ticket_forum: "Crea ticket nel Forum",
      rispondi_ticket_forum: "Risponde ai ticket",
      chiudi_ticket_forum: "Chiude/riapre ticket",
      modera_forum: "Modera Forum (elimina contenuti)",
      gestisci_sezioni_forum: "Gestisce sezioni Forum"
    }
  },
  "Patenti": {
    icon: FileText,
    permessi: {
      visualizza_patenti: "Visualizza le patenti",
      gestisci_patenti: "Rilascia e revoca patenti"
    }
  },
  "Documenti (Municipio)": {
    icon: FileText,
    permessi: {
      visualizza_documenti: "Visualizza documenti",
      gestisci_documenti: "Gestisce documenti",
      crea_documenti_identita: "Crea CDI/Tessere (Segreteria)",
      gestisci_seggi: "Gestisce Seggi Elettorali",
      richiedi_curriculum_municipio: "Richiede curriculum al Municipio"
    }
  },
  "Scuola": {
    icon: GraduationCap,
    permessi: {
      firma_lezioni: "Firma lezioni (Docente)",
      gestisci_voti: "Gestisce voti studenti",
      direzione_scuola: "Direzione Scuola"
    }
  },
  "Ospedale": {
    icon: Stethoscope,
    permessi: {
      visualizza_cartelle_cliniche: "Visualizza cartelle cliniche",
      gestisci_cartelle_cliniche: "Gestisce cartelle cliniche (Medico)"
    }
  },
  "Banca": {
    icon: Landmark,
    permessi: {
      visualizza_conti: "Visualizza conti clienti",
      gestisci_conti: "Gestisce conti e operazioni",
      visualizza_utenti_banca: "Vede lista di tutti i cittadini"
    }
  },
  "Forze dell'Ordine": {
    icon: Shield,
    permessi: {
      inserisci_multe: "Inserisce multe",
      visualizza_fedina_penale: "Visualizza fedina penale",
      visualizza_utenti_multe: "Vede lista di tutti i cittadini"
    }
  },
  "Posta": {
    icon: Receipt,
    permessi: {
      gestisci_pagamenti_multe: "Gestisce pagamenti multe",
      visualizza_utenti_posta: "Vede lista di tutti i cittadini"
    }
  },
  "Negozio": {
    icon: ShoppingBag,
    permessi: {
      visualizza_negozio: "Visualizza negozio",
      gestisci_negozio: "Gestisce prodotti negozio",
      acquista_prodotti: "Può acquistare prodotti"
    }
  },
  "Gazzetta": {
    icon: Newspaper,
    permessi: {
      pubblica_gazzetta: "Pubblica articoli sulla Gazzetta"
    }
  },
  "Dashboard Admin": {
    icon: LayoutDashboard,
    permessi: {
      visualizza_dashboard_admin: "Visualizza Dashboard Admin",
      modifica_scontrini_admin: "Modifica scontrini come Admin",
      modifica_dipendenti_admin: "Modifica dipendenti come Admin"
    }
  },
  "Amministrazione Staff": {
    icon: Users,
    permessi: {
      gestione_utenti_admin: "Gestione completa utenti (crea, modifica, elimina)"
    }
  }
};

// Colori per azienda - basati sull'indice o ID
const COLORI_AZIENDA = [
  { bg: 'bg-blue-100', text: 'text-blue-800', border: 'border-blue-300' },
  { bg: 'bg-purple-100', text: 'text-purple-800', border: 'border-purple-300' },
  { bg: 'bg-green-100', text: 'text-green-800', border: 'border-green-300' },
  { bg: 'bg-amber-100', text: 'text-amber-800', border: 'border-amber-300' },
  { bg: 'bg-red-100', text: 'text-red-800', border: 'border-red-300' },
  { bg: 'bg-cyan-100', text: 'text-cyan-800', border: 'border-cyan-300' },
  { bg: 'bg-pink-100', text: 'text-pink-800', border: 'border-pink-300' },
  { bg: 'bg-indigo-100', text: 'text-indigo-800', border: 'border-indigo-300' },
  { bg: 'bg-teal-100', text: 'text-teal-800', border: 'border-teal-300' },
  { bg: 'bg-orange-100', text: 'text-orange-800', border: 'border-orange-300' },
];

const getColoreAzienda = (aziendaId, aziendeList) => {
  if (!aziendaId || !aziendeList) return COLORI_AZIENDA[0];
  const index = aziendeList.findIndex(a => a.id === aziendaId);
  return COLORI_AZIENDA[index % COLORI_AZIENDA.length];
};

// Flatten per labels
const PERMESSI_LABELS = {};
Object.values(PERMESSI_CATEGORIE).forEach(cat => {
  Object.entries(cat.permessi).forEach(([key, label]) => {
    PERMESSI_LABELS[key] = label;
  });
});

const RuoliAzienda = () => {
  const { isSindacatoAdmin } = useAuth();
  const [ruoli, setRuoli] = useState([]);
  const [aziende, setAziende] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingRuolo, setEditingRuolo] = useState(null);
  const [filterAzienda, setFilterAzienda] = useState('all');
  
  const [formData, setFormData] = useState({
    nome: '',
    azienda_id: '',
    permessi: [],
    livello_gerarchia: 0
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [ruoliRes, aziendeRes] = await Promise.all([
        axios.get(`${API_URL}/ruoli-azienda`),
        axios.get(`${API_URL}/aziende`)
      ]);
      setRuoli(ruoliRes.data);
      setAziende(aziendeRes.data);
    } catch (error) {
      toast.error('Errore nel caricamento dei dati');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingRuolo) {
        await axios.patch(`${API_URL}/ruoli-azienda/${editingRuolo.id}`, {
          nome: formData.nome,
          permessi: formData.permessi,
          livello_gerarchia: formData.livello_gerarchia
        });
        toast.success('Ruolo aggiornato');
      } else {
        await axios.post(`${API_URL}/ruoli-azienda`, formData);
        toast.success('Ruolo creato');
      }
      setDialogOpen(false);
      setEditingRuolo(null);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nel salvataggio');
    }
  };

  const handleDelete = async (ruoloId) => {
    if (!window.confirm('Sei sicuro di voler eliminare questo ruolo?')) return;
    try {
      await axios.delete(`${API_URL}/ruoli-azienda/${ruoloId}`);
      toast.success('Ruolo eliminato');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nell\'eliminazione');
    }
  };

  const handleEdit = (ruolo) => {
    setEditingRuolo(ruolo);
    setFormData({
      nome: ruolo.nome,
      azienda_id: ruolo.azienda_id,
      permessi: ruolo.permessi || [],
      livello_gerarchia: ruolo.livello_gerarchia || 0
    });
    setDialogOpen(true);
  };

  const resetForm = () => {
    setFormData({
      nome: '',
      azienda_id: '',
      permessi: [],
      livello_gerarchia: 0
    });
  };

  const togglePermesso = (permesso) => {
    setFormData(prev => ({
      ...prev,
      permessi: prev.permessi.includes(permesso)
        ? prev.permessi.filter(p => p !== permesso)
        : [...prev.permessi, permesso]
    }));
  };

  const selectAllInCategory = (categoria) => {
    const catPermessi = Object.keys(PERMESSI_CATEGORIE[categoria].permessi);
    const allSelected = catPermessi.every(p => formData.permessi.includes(p));
    
    if (allSelected) {
      // Deseleziona tutti
      setFormData(prev => ({
        ...prev,
        permessi: prev.permessi.filter(p => !catPermessi.includes(p))
      }));
    } else {
      // Seleziona tutti
      setFormData(prev => ({
        ...prev,
        permessi: [...new Set([...prev.permessi, ...catPermessi])]
      }));
    }
  };

  const filteredRuoli = filterAzienda === 'all' 
    ? ruoli 
    : ruoli.filter(r => r.azienda_id === filterAzienda);

  // Trova l'azienda selezionata per suggerire permessi appropriati
  const selectedAzienda = aziende.find(a => a.id === formData.azienda_id);
  const suggestedCategories = selectedAzienda ? getSuggestedCategories(selectedAzienda.tipo) : [];

  function getSuggestedCategories(tipoAzienda) {
    const suggestions = {
      'SCUOLA': ['Scuola', 'Generale'],
      'OSPEDALE': ['Ospedale', 'Generale'],
      'MUNICIPIO': ['Documenti (Municipio)', 'Generale'],
      'BANCA': ['Banca', 'Generale'],
      'POLIZIA': ['Forze dell\'Ordine / Posta', 'Generale'],
      'POSTA': ['Forze dell\'Ordine / Posta', 'Generale'],
    };
    return suggestions[tipoAzienda] || ['Generale'];
  }

  if (!isSindacatoAdmin()) {
    return (
      <div className="text-center py-12">
        <p className="text-slate-500">Accesso non autorizzato</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="ruoli-azienda-page">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="font-serif text-3xl font-bold text-slate-900">Gestione Ruoli e Permessi</h1>
          <p className="text-slate-600 mt-1">Assegna permessi specifici per ogni ruolo e azienda</p>
        </div>
        
        <Dialog open={dialogOpen} onOpenChange={(open) => {
          setDialogOpen(open);
          if (!open) {
            setEditingRuolo(null);
            resetForm();
          }
        }}>
          <DialogTrigger asChild>
            <Button data-testid="create-ruolo-btn" className="gap-2">
              <Plus className="w-4 h-4" />
              Nuovo Ruolo
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {editingRuolo ? 'Modifica Ruolo' : 'Crea Nuovo Ruolo'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="nome">Nome Ruolo</Label>
                  <Input
                    id="nome"
                    data-testid="ruolo-nome-input"
                    value={formData.nome}
                    onChange={(e) => setFormData({...formData, nome: e.target.value})}
                    placeholder="es. Manager, Cassiere, Medico..."
                    required
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="azienda">Azienda</Label>
                  <Select
                    value={formData.azienda_id}
                    onValueChange={(value) => setFormData({...formData, azienda_id: value})}
                    disabled={!!editingRuolo}
                  >
                    <SelectTrigger data-testid="ruolo-azienda-select">
                      <SelectValue placeholder="Seleziona azienda" />
                    </SelectTrigger>
                    <SelectContent>
                      {aziende.map((az) => (
                        <SelectItem key={az.id} value={az.id}>
                          {az.nome} ({az.tipo})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="livello">Livello Gerarchia</Label>
                <p className="text-xs text-slate-500">0 = base, 10 = Direttore (può modificare/eliminare dati)</p>
                <Input
                  id="livello"
                  type="number"
                  min="0"
                  max="10"
                  value={formData.livello_gerarchia}
                  onChange={(e) => setFormData({...formData, livello_gerarchia: parseInt(e.target.value)})}
                />
              </div>

              {/* Permessi organizzati per categoria */}
              <div className="space-y-4">
                <Label>Permessi</Label>
                {selectedAzienda && (
                  <div className="bg-blue-50 border border-blue-200 p-3 rounded-lg">
                    <p className="text-sm text-blue-800">
                      <strong>Suggerimento:</strong> Per un'azienda di tipo "{selectedAzienda.tipo}", 
                      considera i permessi nelle categorie: {suggestedCategories.join(', ')}
                    </p>
                  </div>
                )}
                
                {Object.entries(PERMESSI_CATEGORIE).map(([categoria, { icon: Icon, permessi }]) => {
                  const catPermessi = Object.keys(permessi);
                  const selectedCount = catPermessi.filter(p => formData.permessi.includes(p)).length;
                  const allSelected = selectedCount === catPermessi.length;
                  const isSuggested = suggestedCategories.includes(categoria);
                  
                  return (
                    <div 
                      key={categoria} 
                      className={`p-4 rounded-lg border ${isSuggested ? 'bg-green-50 border-green-200' : 'bg-slate-50 border-slate-200'}`}
                    >
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <Icon className={`w-5 h-5 ${isSuggested ? 'text-green-600' : 'text-slate-600'}`} />
                          <span className="font-medium">{categoria}</span>
                          {isSuggested && (
                            <Badge className="bg-green-100 text-green-800 text-xs">Consigliato</Badge>
                          )}
                          <Badge variant="outline" className="text-xs">
                            {selectedCount}/{catPermessi.length}
                          </Badge>
                        </div>
                        <Button 
                          type="button" 
                          variant="ghost" 
                          size="sm"
                          onClick={() => selectAllInCategory(categoria)}
                        >
                          {allSelected ? 'Deseleziona tutto' : 'Seleziona tutto'}
                        </Button>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        {Object.entries(permessi).map(([key, label]) => (
                          <div key={key} className="flex items-center space-x-2">
                            <Checkbox
                              id={key}
                              checked={formData.permessi.includes(key)}
                              onCheckedChange={() => togglePermesso(key)}
                            />
                            <label htmlFor={key} className="text-sm cursor-pointer">
                              {label}
                            </label>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="flex justify-end gap-2 pt-4 border-t">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  Annulla
                </Button>
                <Button type="submit" data-testid="save-ruolo-btn">
                  {editingRuolo ? 'Salva Modifiche' : 'Crea Ruolo'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Filtro per azienda */}
      <div className="flex items-center gap-4">
        <Label>Filtra per azienda:</Label>
        <Select value={filterAzienda} onValueChange={setFilterAzienda}>
          <SelectTrigger className="w-64">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutte le aziende</SelectItem>
            {aziende.map((az) => (
              <SelectItem key={az.id} value={az.id}>{az.nome}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Legenda permessi */}
      <Card className="bg-slate-50">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium text-slate-600">Categorie Permessi</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-3">
            {Object.entries(PERMESSI_CATEGORIE).map(([categoria, { icon: Icon }]) => (
              <div key={categoria} className="flex items-center gap-1 text-sm text-slate-600">
                <Icon className="w-4 h-4" />
                <span>{categoria}</span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Lista ruoli */}
      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900 mx-auto"></div>
        </div>
      ) : filteredRuoli.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <ShieldCheck className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500">Nessun ruolo trovato</p>
            <p className="text-sm text-slate-400">Crea un nuovo ruolo per iniziare</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {filteredRuoli.map((ruolo) => {
            const colore = getColoreAzienda(ruolo.azienda_id, aziende);
            return (
            <Card key={ruolo.id} data-testid={`ruolo-card-${ruolo.id}`} className={`border-l-4 ${colore.border}`}>
              <CardHeader className="pb-2">
                <div className="flex justify-between items-start">
                  <div>
                    <CardTitle className="text-lg">{ruolo.nome}</CardTitle>
                    <p className={`text-sm font-medium ${colore.text}`}>{ruolo.azienda_nome}</p>
                  </div>
                  <Badge className={`${colore.bg} ${colore.text}`}>
                    {ruolo.livello_gerarchia >= 10 ? 'Direttore' : `Liv. ${ruolo.livello_gerarchia}`}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent>
                <div className="mb-4">
                  <p className="text-xs text-slate-500 uppercase mb-2">Permessi ({ruolo.permessi?.length || 0})</p>
                  <div className="flex flex-wrap gap-1 max-h-24 overflow-y-auto">
                    {ruolo.permessi?.length > 0 ? (
                      ruolo.permessi.map((p) => (
                        <Badge key={p} variant="secondary" className="text-xs">
                          {PERMESSI_LABELS[p] || p}
                        </Badge>
                      ))
                    ) : (
                      <span className="text-sm text-slate-400">Nessun permesso</span>
                    )}
                  </div>
                </div>
                
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleEdit(ruolo)}
                    className="flex-1"
                  >
                    <Edit2 className="w-4 h-4 mr-1" />
                    Modifica
                  </Button>
                  <Button
                    size="sm"
                    variant="destructive"
                    onClick={() => handleDelete(ruolo.id)}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default RuoliAzienda;
